import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { apiFetch } from "../api/client";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const notify = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setErrors({ email: "Email is required." });
      notify("error", { title: "Validation", text: "Email is required." });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const res = await apiFetch(`/api/account/forgot-password`, {
        skipTenant: true,
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        let parsed = null;
        try {
          parsed = await res.json();
        } catch (err) {
          notify("error", { title: "Request failed", text: res.statusText });
          setLoading(false);
          return;
        }
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data?.succeeded) {
        notify("success", { title: "Reset token sent", text: data.message || "Check your email for the reset token." });
        // Navigate to reset password page so user can enter token and new password
        navigate(`/reset-password?email=${encodeURIComponent(email)}`);
      } else {
        const note = mapApiErrorToNotification(data, res.status);
        notify("error", note);
      }
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-12 w-12 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <div className="text-white font-medium">Sending reset token...</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 h-screen items-center place-items-center">
        <div className="w-full max-w-md space-y-8 p-6 sm:p-10 rounded-lg">
          <div>
            <img
              className="mx-auto h-20 w-auto"
              src={require("../assets/stokraze.png")}
              alt="Your Company"
            />
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Forgot password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">Enter your account email and we'll send a reset token to it.</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Email</label>
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => { setEmail(e.target.value); setErrors({ ...errors, email: null }); }}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Email address" disabled={loading} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
            </div>

            <div>
              <button type="submit" disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                aria-busy={loading}
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                )}
                {loading ? "Sending..." : "Send reset token"}
              </button>
            </div>
          </form>

          <p className="mt-4 text-center text-sm text-gray-600">
            <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">
              Back to login
            </Link>
          </p>
        </div>

        <div className="flex justify-center order-first sm:order-last p-4">
          {/* Mobile: small constrained image */}
          <img className="block sm:hidden max-w-full h-auto w-11/12 max-w-sm object-contain" src={require("../assets/SignUp2.png")} alt="" />

          {/* Desktop / laptop: use original natural size */}
          <img className="hidden sm:block" src={require("../assets/SignUp2.png")} alt="" />
        </div>
      </div>
    </>
  );
}
